// src/components/gestionEvento/Categorias/Dialogs.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Dialog reutilizable: Añadir Categoría (usa el mismo estilo que los otros modales).
 * - fullScreen en móvil
 * - Header sticky con "X"
 * - Footer sticky con botones estilizados
 *
 * REQUISITO: el formulario interno debe tener id="add-categoria-form"
 * para que el botón del footer dispare el submit (requestSubmit).
 */
export function AddCategoriaDialog({
  open,
  onClose,
  children, // aquí renderizas tu <AddCategoriaForm />
  title = 'Añadir Categoría Personalizada',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const submitForm = () => {
    const form = document.getElementById('add-categoria-form');
    if (form) form.requestSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          m: 0,
          borderRadius: isMobile ? 0 : 2,
          height: isMobile ? '100%' : 'auto',
        },
      }}
    >
      {/* Header sticky */}
      <Box
        sx={(t) => ({
          position: 'sticky',
          top: 0,
          zIndex: 3,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${t.palette.divider}`,
          bgcolor: t.palette.background.paper,
        })}
      >
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} aria-label="cerrar">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Contenido con divisores */}
      <DialogContent dividers>{children}</DialogContent>

      {/* Footer sticky */}
      <DialogActions
        sx={(t) => ({
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${t.palette.divider}`,
          bgcolor: t.palette.background.paper,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
        })}
      >
        <Button
          onClick={onClose}
          sx={(t) => ({
            borderRadius: 999,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
            color: t.palette.secondary.main,
            border: `1px solid ${t.palette.secondary.main}`,
            '&:hover': { bgcolor: t.palette.action.hover },
          })}
        >
          Cancelar
        </Button>
        <Button
          onClick={submitForm}
          variant="contained"
          sx={() => ({
            borderRadius: 999,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 700,
          })}
        >
          Añadir Categoría
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Dialog: Confirmación de eliminación de categorías (masivo o unitario)
 */
export function ConfirmDeleteDialog({
  open,
  onClose,
  onConfirm,
  count = 0,
  loading = false,
}) {
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="confirm-delete-dialog-title">
      <DialogTitle id="confirm-delete-dialog-title">Confirmar Eliminación</DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-delete-dialog-description">
          ¿Estás seguro de eliminar {count} categoría{count > 1 ? 's' : ''} seleccionada{count > 1 ? 's' : ''}? Esta acción no se puede deshacer.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancelar
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained" disabled={loading}>
          {loading ? 'Eliminando…' : 'Eliminar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Dialog: Pedir nombre de archivo para exportar CSV
 */
export function FilenameDialog({
  open,
  onClose,
  onConfirm,
  children, // <TextField .../> viene desde el manager para mantener el estado del filename
  confirming = false,
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nombre del Archivo CSV</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button onClick={onConfirm} color="primary" variant="contained" disabled={confirming}>
          {confirming ? 'Preparando…' : 'Descargar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Dialog: Errores de importación (tabla)
 */
export function ImportErrorsDialog({
  open,
  onClose,
  rows = [],
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Errores al Importar</DialogTitle>
      <DialogContent>
        <DialogContentText>Las siguientes categorías no se pudieron agregar:</DialogContentText>
        <Box mt={2}>
          <TableContainer component={Paper}>
            <Table aria-label="tabla de errores de importación">
              <TableHead>
                <TableRow>
                  <TableCell>Fila</TableCell>
                  <TableCell>#Cat</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Edad Mínima</TableCell>
                  <TableCell>Edad Máxima</TableCell>
                  <TableCell>Fecha de Corte</TableCell>
                  <TableCell>Razón</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((error, index) => (
                  <TableRow key={index}>
                    <TableCell>{error.fila}</TableCell>
                    <TableCell
                      sx={{
                        bgcolor: error.reasons?.some((r) => r.includes('Duplicado en #Cat')) ? 'error.light' : 'inherit',
                      }}
                    >
                      {error.catNumber || 'N/A'}
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: error.reasons?.some((r) => r.includes('Duplicado en código')) ? 'error.light' : 'inherit',
                      }}
                    >
                      {error.codigo || 'N/A'}
                    </TableCell>
                    <TableCell>{error.nombre || 'N/A'}</TableCell>
                    <TableCell
                      sx={{
                        bgcolor: error.reasons?.some((r) => r.includes('Edad mínima') || r.includes('Edad máxima')) ? 'error.light' : 'inherit',
                      }}
                    >
                      {error.edadMin || 'N/A'}
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: error.reasons?.some((r) => r.includes('Edad mínima') || r.includes('Edad máxima')) ? 'error.light' : 'inherit',
                      }}
                    >
                      {error.edadMax || 'N/A'}
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: error.reasons?.some((r) => r.includes('Fecha de corte')) ? 'error.light' : 'inherit',
                      }}
                    >
                      {error.fechaCorte || 'N/A'}
                    </TableCell>
                    <TableCell>{Array.isArray(error.reasons) ? error.reasons.join(', ') : error.reasons}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No hay errores para mostrar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Aceptar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
